import { Dropdown, Menu as AntdMenu, Tooltip } from 'antd';
import {
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';
import { NavigationService } from 'services';
import ChangePassword from 'containers/auth/ChangePassword';
import { IconSvg } from 'shared/icon';
import { useLogout } from 'hooks';
import { selectLatestReleaseNote } from 'features/releaseNote/selectors';
import { useAppSelector } from 'store/hooks';
import { selectAuthUser } from 'app/auth/selectors';
import ReleaseNoteModal from 'features/releaseNote/components/ReleaseNoteModal';
import { StyledAccountWrapper, StyledListItemHeader } from 'app/layout/components/Layout.styled';
import ConfirmModal from 'shared/modal/ConfirmModal';

export interface MenuProps {
  isSearch?: boolean
}

const Menu = ({ isSearch = true }:MenuProps) => {
  const [dropDownVisible, setDropDownVisible] = useState(false);
  const [action, setAction] = useState({
    type: '',
    visible: false,
  });
  const { t } = useTranslation();
  const { logout } = useLogout();
  const latestReleaseNote = useAppSelector(selectLatestReleaseNote);
  const user = useAppSelector(selectAuthUser);

  const { colors } = useTheme();

  const menuItems = useMemo(
    () => [
      user && {
        key: 'name',
        label: `${user.firstName} ${user.lastName}`,
      },
      {
        key: 'account',
        label: (
          <div onClick={() => NavigationService(isSearch ? '/' : '/noAccessAccount')}>
            <IconSvg icon="AccountIcon" width="1.2rem" height="1.2rem" />
            {t('myAccount')}
          </div>
        ),
      },
      {
        key: 'changePassword',
        label: (
          <div>
            <IconSvg icon="UpdateAccount2FAIcon" width="1.2rem" height="1.2rem" />
            {t('changePass')}
          </div>
        ),
      },
      {
        key: 'releaseNote',
        label: (
          <div>
            <IconSvg icon="SeoNotificationIcon" width="1.2rem" height="1.2rem" />
            {t('releaseNotes')}
          </div>
        ),
      },
      {
        key: 'logout',
        label: (
          <div>
            <IconSvg icon="LogoutIcon" />
            {t('logOut')}
          </div>
        ),
      },
    ],
    [isSearch, t, user],
  );

  const onClose = () => setAction({
    type: '',
    visible: false,
  });

  const handleCloseReleaseNote = () => {
    onClose();
  };

  const actionTypes = {
    logout: <ConfirmModal visible={action.visible} changeModalState={onClose} text={t('confirm:logout')} handleConfirm={logout} />,
    changePassword: <ChangePassword visible={action.visible} changeModalState={onClose} />,
    releaseNote: <ReleaseNoteModal visible={action.visible} closeModal={handleCloseReleaseNote} />,
  } as const;

  const handleMenuClick = ({ key }: {key:string}) => {
    if (Object.keys(actionTypes).includes(key)) {
      setAction({
        type: key,
        visible: true,
      });
    }
    setDropDownVisible(false);
  };

  const menu = () => (
    <StyledAccountWrapper>
      <AntdMenu onClick={handleMenuClick} items={menuItems} />
    </StyledAccountWrapper>
  );

  type ActionType = keyof typeof actionTypes

  const Modal = action.type ? actionTypes[action.type as ActionType] : null;

  useEffect(() => {
    if (!_.isEmpty(latestReleaseNote) && !latestReleaseNote?.isSeen) {
      setAction({
        type: 'releaseNote',
        visible: true,
      });
    }
  }, [latestReleaseNote]);

  return (
    <>
      {Modal}
      <Dropdown open={dropDownVisible} onOpenChange={setDropDownVisible} popupRender={menu} placement="bottom" trigger={['click']}>
        <StyledListItemHeader>
          <Tooltip placement="bottom" title={t('account')}>
            <div>
              <IconSvg icon="AccountIcon" width="1rem" height="1rem" color={colors.black} />
            </div>
          </Tooltip>
        </StyledListItemHeader>
      </Dropdown>
    </>
  );
};

export default Menu;
